import React from 'react';

import { cn } from '@/lib/utils';

interface StepProgressProps {
    /** Zero-based index of the active step. */
    activeIndex: number;
    /** Total number of steps. */
    total: number;
    /** Highest step index reached — segments up to it are clickable. */
    maxReachedIndex?: number;
    /** Navigate to an already-reached step. */
    onStepSelect?: (index: number) => void;
    className?: string;
}

/**
 * Segmented progress bar across the top of the onboarding card — one segment
 * per step, filled up to and including the active one. Segments for steps the
 * user has already reached act as navigation.
 */
const StepProgress: React.FC<StepProgressProps> = ({
    activeIndex,
    total,
    maxReachedIndex,
    onStepSelect,
    className,
}) => (
    // role="group" (not progressbar — that would flatten the clickable
    // segments to presentational) + sr-only text carries "Step X of Y".
    <div
        role="group"
        aria-label="Onboarding progress"
        className={cn('flex items-center gap-1.5', className)}
    >
        <span className="sr-only" aria-live="polite">
            {`Step ${activeIndex + 1} of ${total}`}
        </span>
        {Array.from({ length: total }, (_, i) => {
            const reached = i <= (maxReachedIndex ?? activeIndex);
            const clickable = !!onStepSelect && reached && i !== activeIndex;
            const bar = (
                <div
                    className={cn(
                        'h-1 w-full rounded-full transition-colors duration-300',
                        i <= activeIndex
                            ? 'bg-primary'
                            : reached
                                ? 'bg-primary/30'
                                : 'bg-border',
                    )}
                />
            );

            return clickable ? (
                <button
                    key={i}
                    type="button"
                    onClick={() => onStepSelect(i)}
                    aria-label={`Go to step ${i + 1}`}
                    className="flex-1 cursor-pointer py-1.5 transition-opacity hover:opacity-70"
                >
                    {bar}
                </button>
            ) : (
                <div key={i} className="flex-1 py-1.5">
                    {bar}
                </div>
            );
        })}
    </div>
);

export default StepProgress;
